import { Modal, Grid, Text } from '@simplywallst/ui-core';

import { useSnackbar } from '@/features/snackbar';

import type { UnmatchedHolding } from '@/hooks/portfolio/usePortfolio';

import { useRemoveHoldingMutation } from './hooks/useRemoveHoldingMutation';

interface Props {
  unmatchedHolding: UnmatchedHolding;
  onSuccess: () => void;
  onCancel: () => void;
  onError: () => void;
}

export const RemoveUnmatchedHoldingModal = ({
  unmatchedHolding,
  onSuccess,
  onCancel,
  onError,
}: Props) => {
  const { addSnackbar } = useSnackbar();
  const { mutate, status: mutationStatus } = useRemoveHoldingMutation({
    onSuccess() {
      onSuccess();
      addSnackbar({
        type: 'positive',
        message: `Removed ${unmatchedHolding.symbol} from your portfolio.`,
        lifeTime: 2000,
      });
    },
    onError() {
      onError();
      addSnackbar({
        type: 'negative',
        message: `Failed to remove ${unmatchedHolding.symbol} from your portfolio.`,
        lifeTime: 2000,
      });
    },
  });

  return (
    <Modal
      data-cy-id="remove-unmatched-holding-confirmation-modal"
      title={`Removing ${unmatchedHolding.symbol}`}
      primaryLabel={`Remove ${unmatchedHolding.symbol}`}
      onPrimaryClick={() => mutate(unmatchedHolding)}
      primaryLoading={mutationStatus === 'pending'}
      onSecondaryClick={onCancel}
      secondaryLabel="Cancel"
    >
      <Grid gap="16px">
        <Text typography="small" color="spaceGray">
          You're about to remove {unmatchedHolding.symbol} from this portfolio.
        </Text>
        <Text typography="small" color="spaceGray">
          You won't be able to add it back in or match it again.
        </Text>
        <Text typography="small" color="spaceGray">
          Are you sure?
        </Text>
      </Grid>
    </Modal>
  );
};
